import { normalize } from 'normalizr'
import { Schemas } from 'redux-modules/schema'
import { updateEntities, deleteEntity } from 'redux-modules/entity'

import api from 'api'

const FETCH_REQUEST = 'realhub/imageLibrary/FETCH_REQUEST'
const FETCH_SUCCESS = 'realhub/imageLibrary/FETCH_SUCCESS'
const FETCH_FAILURE = 'realhub/imageLibrary/FETCH_FAILURE'
const CREATE_REQUEST = 'realhub/imageLibrary/CREATE_REQUEST'
const CREATE_SUCCESS = 'realhub/imageLibrary/CREATE_SUCCESS'
const CREATE_FAILURE = 'realhub/imageLibrary/CREATE_FAILURE'
const UPDATE_REQUEST = 'realhub/imageLibrary/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realhub/imageLibrary/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realhub/imageLibrary/UPDATE_FAILURE'
const DELETE_REQUEST = 'realhub/imageLibrary/DELETE_REQUEST'
const DELETE_SUCCESS = 'realhub/imageLibrary/DELETE_SUCCESS'
const DELETE_FAILURE = 'realhub/imageLibrary/DELETE_FAILURE'

// Initial State
const initialState = {
  loaded: false,
  loadedForKeys: [],
  loading: false,
  creating: false,
  updating: false,
  deleting: false,
  errors: [],
}

// Actions
export function fetchRequest(payload = {}){
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
  }
}

export function fetchSuccess(){
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors = []){
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(){
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(){
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors = []){
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(){
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(){
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors = []){
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest(){
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(){
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors = []){
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Helper Functions
const handleError = errors => errors || ['Something went wrong']

// Action Creators
export function loadImageLibraries(options){
  const key = options && options.owner_type && options.owner_id ? `${options.owner_type}${options.owner_id}` : null

  return (dispatch, getState) => {
    const state = getState()
    const loadedForKeys = [...state.imageLibraries.loadedForKeys]
    if (key && !loadedForKeys.includes(key)){
      loadedForKeys.push(key)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/admin/image_libraries.json', options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.IMAGE_LIBRARY_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))
      })

    return promise
  }
}

export function loadImageLibrary(imageLibraryId, options){
  const { entityKey } = options

  return (dispatch, getState) => {
    const state = getState()
    const loadedForKeys = [...state.imageLibraries.loadedForKeys]
    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api(`/admin/image_libraries/${imageLibraryId}.json`, options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.IMAGE_LIBRARY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

export function createImageLibrary(imageLibrary){
  const config = {
    method: 'POST',
    body: JSON.stringify({
      image_library: imageLibrary,
    }),
  }

  return (dispatch) => {
    // Set Loading
    dispatch(createRequest())

    const promise = api('/admin/image_libraries.json', {}, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.IMAGE_LIBRARY)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(createFailure(errors))
      })

    return promise
  }
}

export function deleteImageLibrary(imageLibrary){
  const config = {
    method: 'DELETE',
  }

  return (dispatch) => {
    // Set Loading
    dispatch(deleteRequest())

    const promise = api(`/admin/image_libraries/${imageLibrary.id}.json`, {}, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }
        const normalizedJson = normalize(json, Schemas.IMAGE_LIBRARY)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(deleteFailure(errors))
      })
    return promise
  }
}

export function updateImageLibrary(imageLibrary, options){
  const config = {
    method: 'PUT',
    body: JSON.stringify({
      image_library: imageLibrary,
    }),
  }

  return (dispatch) => {
    // Set Loading
    dispatch(updateRequest())

    const promise = api(`/admin/image_libraries/${imageLibrary.id}.json`, {}, config, options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }
        const normalizedJson = normalize(json, Schemas.IMAGE_LIBRARY)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))

        return { success: false, result: errors }
      })
    return promise
  }
}

// Reducers
export default function reducer(state = initialState, action = {}){
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loading: true,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors,
      }
    case CREATE_REQUEST:
      return { ...state, creating: true }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors,
      }
    case UPDATE_REQUEST:
      return { ...state, updating: true }
    case UPDATE_SUCCESS:
      return { ...state, updating: false }
    case UPDATE_FAILURE:
      return { ...state, updating: false }
    case DELETE_REQUEST:
      return { ...state, updating: true }
    case DELETE_SUCCESS:
      return { ...state, updating: false }
    case DELETE_FAILURE:
      return { ...state, updating: false }
    default:
      return state
  }
}
